import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { URL } from "../../api";
import axiosInstance from "../../Request";
import { Card, CardHeader, CardBody, Col, Row, Table, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { calculateApr } from "../../util/apr";

import Installment from "../Installment/Installment";

const Profile = ({ isPayment = false }) => {
  const { cid, lid } = useParams();
  const navigate = useNavigate();
  const [loanList, setLoanList] = useState([]);
  const [walletRows, setWalletRows] = useState([]);
  const [repaymentRows, setRepaymentRows] = useState([]);
  // Customer view already has its own Customer section, so the redundant "Customer"
  // tab is hidden there and Lead is the default; the payment view keeps all tabs.
  const [activeTab, setActiveTab] = useState(isPayment ? "customer" : "lead");
  const [customerDetails, setCustomerDetails] = useState(null);

  // Sidebar "Installment & Collection" / "Leads" links open the matching tab here.
  useEffect(() => {
    const onOpenTab = (e) => {
      if (e?.detail) setActiveTab(e.detail);
    };
    window.addEventListener("open-lead-tab", onOpenTab);
    return () => window.removeEventListener("open-lead-tab", onOpenTab);
  }, []);

  // Customer detail for the Customer tab.
  useEffect(() => {
    if (!cid) return;
    axiosInstance
      .get(`${URL.CUSTOMER_DETAILS}?customer_id=${cid}&lead_id=${lid}`)
      .then((res) => setCustomerDetails(res.data))
      .catch(() => setCustomerDetails(null));
  }, [cid, lid]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const response = await axiosInstance.get(
          URL.LEAD_LIST + `?customer_id=${cid}`
        );
        const data = response.data;
        setLoanList(data);
      } catch (error) {
        console.error("Error fetching customer details:", error);
      }
    };

    fetchCustomerDetails();
  }, [cid, lid]);

  // Total collection sourced from the payment table + amort adjustment (backend).
  // scheme default: PIF for payment customers, FIP for regular loans — toggled in the Collection tab.
  const [collectionTotal, setCollectionTotal] = useState(null);
  const [collectionScheme, setCollectionScheme] = useState(isPayment ? "pif" : "fip");

  // Payment full-view only: pull wallet + repayment data for the overview summary.
  useEffect(() => {
    if (!isPayment || !cid) return;
    const fetchPaymentSummary = async () => {
      try {
        const [wallet, repayment] = await Promise.all([
          axiosInstance.get(URL.PAYMENT_WALLET_HISTORY, { params: { customer_id: cid } }),
          axiosInstance.get(URL.PAYMENT_REPAYMENT_LIST, { params: { customer_id: cid } }),
        ]);
        setWalletRows(Array.isArray(wallet.data) ? wallet.data : []);
        setRepaymentRows(Array.isArray(repayment.data) ? repayment.data : []);
      } catch (error) {
        console.error("Error fetching payment summary:", error);
        setWalletRows([]);
        setRepaymentRows([]);
      }
    };
    fetchPaymentSummary();
  }, [isPayment, cid]);

  // Collection total (both customer + payment views) — refetches when the FIP/PIF scheme is toggled.
  useEffect(() => {
    if (!cid) return;
    axiosInstance
      .get(URL.PAYMENT_COLLECTION_TOTAL, { params: { customer_id: cid, scheme: collectionScheme } })
      .then((res) => setCollectionTotal(res.data || null))
      .catch(() => setCollectionTotal(null));
  }, [cid, collectionScheme]);

  const activeLoan = loanList.find(
    (loan) => String(loan.lead_id) === String(lid)
  );

  // Derived payment figures (wallet history is ordered oldest -> newest).
  const num = (v) => Number(v || 0);
  const money = (v) =>
    num(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const availableLimit = walletRows.length
    ? num(walletRows[walletRows.length - 1].limit)
    : 0;
  // Match any withdrawal spelling ("withdrawl" / "withdrawal" / "withdraw").
  const withdrawalOnly = walletRows
    .filter((r) => (r.payType || "").toLowerCase().includes("withdraw"))
    .reduce((sum, r) => sum + num(r.amount), 0);
  const walletCreatedRow =
    walletRows.find((r) => (r.payType || "").toLowerCase() === "loginfee") || walletRows[0];
  const walletCreatedDate = walletCreatedRow ? walletCreatedRow.createdDate : null;
  // ProcessingFee wallet rows store fee + 18% GST combined; split them for display.
  const PROC_GST_RATE = 0.18;
  const processingFeeInclGst = walletRows
    .filter((r) => (r.payType || "").toLowerCase() === "processingfee")
    .reduce((sum, r) => sum + num(r.amount), 0);
  const processingAmount = processingFeeInclGst / (1 + PROC_GST_RATE);
  const processingGst = processingFeeInclGst - processingAmount;

  // Outstanding. The repayment rows are now the withdrawal EMI schedule (emi_schedule): one EMI per
  // withdrawal with principal/interest and their outstanding buckets. Principal outstanding is still
  // taken from the wallet (withdrawn principal + processing fee + GST) so a fresh withdrawal is
  // counted immediately; interest comes from the EMI rows. There is no penal in this model.
  const activeBills = repaymentRows.filter((r) => Number(r.emiStatus) === 1);
  const principalOutstanding = withdrawalOnly + processingFeeInclGst;
  const interestOutstanding = activeBills.reduce((s, r) => s + num(r.outstandingInterest), 0);
  const penalCharge = 0;
  const totalOutstanding = principalOutstanding + interestOutstanding + penalCharge;

  // Totals across all EMIs: principal ever drawn. Total collection now comes from the payment table +
  // amort_adjustment_pif (backend), matching the dashboard; fall back to the EMI-derived figure.
  const totalLoanAmount = repaymentRows.reduce((s, r) => s + num(r.principal), 0);
  const totalCollection =
    collectionTotal && collectionTotal.totalCollection != null
      ? num(collectionTotal.totalCollection)
      : repaymentRows.reduce(
          (s, r) => s + (num(r.principal) + num(r.interest) - num(r.outstandingAmount)),
          0
        );

  // APR fallback: backend value wins; otherwise compute (RBI reducing-balance
  // IRR). The upfront deduction that reduces net disbursal = processing fee +
  // GST — taken from the wallet ProcessingFee rows, else from the loan's own
  // processing-fee + GST fields. Tenure is in DAYS, so months come from
  // number_of_emi (else derived from the 30-day monthly cycle).
  const aprProcessingFee =
    processingFeeInclGst ||
    num(activeLoan?.loan_processing_fee) +
      num(activeLoan?.c_gst) +
      num(activeLoan?.i_gst) +
      num(activeLoan?.s_gst);
  const computedApr = activeLoan
    ? calculateApr({
        loanAmount: activeLoan.loan_amount,
        roi: activeLoan.roi,
        tenureMonths:
          Number(activeLoan.number_of_emi) ||
          Math.max(1, Math.round(Number(activeLoan.tenure || 30) / 30)),
        processingFee: aprProcessingFee,
      })
    : 0;
  const displayApr = activeLoan?.apr || computedApr || "-";

  // Total withdrawn for a withdrawal = withdrawn principal + processing fee + GST.
  // (processingFeeInclGst already bundles processing amount + 18% GST.)
  const totalWithdrawn = withdrawalOnly + processingAmount + processingGst;

  const getBadgeColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "disbursed": return "bg-green";
      case "closed": return "bg-dark";
      case "under processing": return "bg-primary";
      default: return "bg-info";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dateStr.split("T")[0];
  };

  const handleRowClick = (leadId) => {
    navigate(`/customer/loans/${cid}/${leadId}`);
    window.location.reload();
  };

  const TABS = [
    // Customer tab only in the payment view; the customer view shows it in its own section.
    ...(isPayment ? [{ key: "customer", label: "Customer" }] : []),
    { key: "lead", label: "Lead" },
    // Collection + Installment merged into one tab.
    { key: "installment", label: "Installment & Collection" },
  ];

  return (
    <Card className="bg-secondary shadow mb-4">
      <CardHeader className="bg-white border-0">
        <Nav tabs>
          {TABS.map((t) => (
            <NavItem key={t.key}>
              <NavLink
                className={activeTab === t.key ? "active" : ""}
                onClick={() => setActiveTab(t.key)}
                style={{ cursor: "pointer" }}
              >
                {t.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </CardHeader>
      <CardBody>
        <TabContent activeTab={activeTab}>
          {/* ---- CUSTOMER ---- */}
          <TabPane tabId="customer">
            {customerDetails?.customer ? (
              <Row>
                <Col md="4" className="mb-2"><strong>Name:</strong> {customerDetails.customer.borrowerName || "-"}</Col>
                <Col md="4" className="mb-2"><strong>Mobile:</strong> {customerDetails.customer.mobileNumber || "-"}</Col>
                <Col md="4" className="mb-2"><strong>PAN:</strong> {customerDetails.customer.panNumber || "-"}</Col>
                <Col md="4" className="mb-2"><strong>Email:</strong> {customerDetails.customer.email || "-"}</Col>
                <Col md="4" className="mb-2"><strong>DOB:</strong> {formatDate(customerDetails.customer.dob)}</Col>
                <Col md="4" className="mb-2"><strong>Aadhaar:</strong> {customerDetails.customer.aadhaarNumber || "-"}</Col>
                <Col md="4" className="mb-2"><strong>Customer ID:</strong> {cid}</Col>
              </Row>
            ) : (
              <div className="text-muted">No customer details available.</div>
            )}
          </TabPane>

          {/* ---- LEAD ---- */}
          <TabPane tabId="lead">
          {activeLoan && (
            <Card className="mb-4" style={{ borderRadius: "0.5rem" }}>
              <CardHeader style={{ backgroundColor: "#e9ecef", color: "#343a40" }}>
                <h4 className="mb-0">
                  Lead Overview (Lead: {activeLoan.lead_code || activeLoan.lead_id}
                  {(activeLoan.loan_code || activeLoan.loan_id || activeLoan.id) != null && <> | Loan: {activeLoan.loan_code || activeLoan.loan_id || activeLoan.id}</>}
                  {activeLoan.customer_code && <> | Customer: {activeLoan.customer_code}</>})
                </h4>
              </CardHeader>
              <CardBody style={{ backgroundColor: "#e9ecef", color: "#343a40" }}>
                {isPayment ? (
                  <Row>
                    {/* Merchant loan amounts (from the loan, not a wallet) */}
                    <Col md="4" className="mb-2"><strong>Loan Amount:</strong> ₹{money(activeLoan.loan_amount)}</Col>
                    <Col md="4" className="mb-2"><strong>Disbursed Amount:</strong> ₹{money(activeLoan.disbursed_amount)}</Col>
                    <Col md="4" className="mb-2"><strong>EMI Amount:</strong> ₹{money(activeLoan.emi_amount)}</Col>
                    {/* Fee + GST (from the loan) */}
                    <Col md="4" className="mb-2"><strong>Processing Amount:</strong> ₹{money(activeLoan.loan_processing_fee)}</Col>
                    <Col md="4" className="mb-2"><strong>GST:</strong> ₹{money(num(activeLoan.c_gst) + num(activeLoan.s_gst) + num(activeLoan.i_gst))}</Col>
                    <Col md="4" className="mb-2"><strong>Total Collection:</strong> ₹{money(collectionTotal ? num(collectionTotal.principalCollection) + num(collectionTotal.interestCollection) + num(collectionTotal.feeCollection) : totalCollection)}</Col>
                    {/* Outstanding (from the EMI schedule) */}
                    <Col md="4" className="mb-2"><strong>Total Outstanding:</strong> ₹{money(activeLoan.total_pending_emi_amount)}</Col>
                    <Col md="4" className="mb-2"><strong>Principal Outstanding:</strong> ₹{money(activeLoan.total_principal)}</Col>
                    <Col md="4" className="mb-2"><strong>Interest Outstanding:</strong> ₹{money(activeLoan.total_interest)}</Col>
                    {/* Terms */}
                    <Col md="4" className="mb-2"><strong>Tenure:</strong> {activeLoan.tenure || "-"} (Days)</Col>
                    <Col md="4" className="mb-2"><strong>APR :</strong> {displayApr}%</Col>
                    <Col md="4" className="mb-2"><strong>ROI:</strong> {activeLoan.roi || "-"}%</Col>
                    <Col md="4" className="mb-2"><strong>ROI Type:</strong> Flat</Col>
                    <Col md="4" className="mb-2"><strong>Frequency:</strong> {activeLoan.repayment_type || "Bullet"}</Col>
                    {/* Type & dates */}
                    <Col md="4" className="mb-2"><strong>Loan Type:</strong> Merchant Loan</Col>
                    <Col md="4" className="mb-2"><strong>Disbursed Date:</strong> {formatDate(activeLoan.disbursed_date)}</Col>
                    <Col md="4" className="mb-2"><strong>Close Date:</strong> {formatDate(activeLoan.close_date)}</Col>
                  </Row>
                ) : (
                  <Row>
                    <Col md="4" className="mb-2"><strong>Loan Amount:</strong> ₹{activeLoan.loan_amount}</Col>
                    <Col md="4" className="mb-2"><strong>Tenure:</strong> {activeLoan.tenure || "-"} (Days)</Col>
                    <Col md="4" className="mb-2"><strong>APR :</strong> {displayApr}%</Col>
                    <Col md="4" className="mb-2"><strong>Disbursed Amount:</strong> ₹{activeLoan.disbursed_amount}</Col>
                    <Col md="4" className="mb-2"><strong>Frequency:</strong> {activeLoan.repayment_type || "-"}</Col>
                    <Col md="4" className="mb-2"><strong>No. of EMIs:</strong> {activeLoan.number_of_emi || "-"}</Col>
                    <Col md="4" className="mb-2"><strong>ROI Type:</strong> {activeLoan.roi_type || "-"}</Col>
                    <Col md="4" className="mb-2"><strong>ROI:</strong> {activeLoan.roi || "-"}%</Col>
                    <Col md="4" className="mb-2"><strong>EMI Amount:</strong> ₹{activeLoan.emi_amount}</Col>
                    <Col md="4" className="mb-2">
                      <strong>Processing Fee:</strong>{" "}
                      {activeLoan.processing_fee_charge
                        ? activeLoan.processing_fee_charge
                        : activeLoan.loan_processing_fee && activeLoan.loan_amount
                        ? ((activeLoan.loan_processing_fee / activeLoan.loan_amount) * 100).toFixed(2)
                        : '0'}%
                    </Col>
                    <Col md="4" className="mb-2">
                      <strong>Processing Amount:</strong> {activeLoan.loan_processing_fee || '0'}
                    </Col>
                    <Col md="4" className="mb-2"><strong>GST:</strong> ₹{(activeLoan.c_gst || 0) + (activeLoan.i_gst || 0) + (activeLoan.s_gst || 0)}</Col>
                    <Col md="4" className="mb-2"><strong>Lead Status:</strong> {activeLoan.lead_status}</Col>
                    <Col md="4" className="mb-2"><strong>Loan Type:</strong> {activeLoan.loan_type}</Col>
                    <Col md="4" className="mb-2"><strong>Broken Amount:</strong> ₹{activeLoan.broken_amount || "0"}</Col>
                    <Col md="4" className="mb-2"><strong>Total Outstanding:</strong> ₹{activeLoan.total_pending_emi_amount || "0"}</Col>
                    <Col md="4" className="mb-2"><strong>Principal Outstanding:</strong> ₹{withdrawalOnly > 0 ? money(principalOutstanding) : (activeLoan.total_principal || "0")}</Col>
                    <Col md="4" className="mb-2"><strong>Interest Outstanding:</strong> ₹{activeLoan.total_interest || "0"}</Col>
                    <Col md="4" className="mb-2"><strong>Approved Date:</strong> {formatDate(activeLoan.approved_date)}</Col>
                    <Col md="4" className="mb-2"><strong>Disbursed Date:</strong> {formatDate(activeLoan.disbursed_date)}</Col>
                    <Col md="4" className="mb-2"><strong>Close Date:</strong> {formatDate(activeLoan.close_date)}</Col>
                    <Col md="4" className="mb-2"><strong>UTR:</strong> {activeLoan.utr || "-"}</Col>
                    <Col md="4" className="mb-2"><strong>Other Deduction:</strong> {activeLoan.other_deduction || "-"}</Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          )}

          {!isPayment && (
          <Table className="align-items-center table-flush" responsive>
            <thead className="thead-light">
              <tr>
                <th scope="col">LID</th>
                <th scope="col">Application Id</th>
                 <th scope="col">Partner LID</th>
                <th scope="col">Partner Name</th>
                <th scope="col">Lead Status</th>
                <th scope="col">Loan Amount</th>
                <th scope="col">EMI Amt</th>
                <th scope="col">Disbursed Date</th>
                <th scope="col">Created At</th>
              </tr>
            </thead>
            <tbody>
              {loanList && loanList.length > 0 ? (
                loanList.map((loan, index) => {
                  const isActiveLoan = String(loan.lead_id) === String(lid);
                  return (
                    <tr
                      key={loan.id || index}
                      onClick={() => handleRowClick(loan.lead_id)}
                      className={`clickable-row ${isActiveLoan ? "table-danger font-weight-bold" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{loan.lead_id}</td>
                      <td>{loan.application_id}</td>
                       <td>{loan.partner_id}</td>
                      <td>{loan.partner_name || "-"}</td>
                      <td>
                        <Badge color="" className="badge-dot mr-4">
                          <i className={getBadgeColor(loan.lead_status)} />
                          {loan.lead_status || "Pending"}
                        </Badge>
                      </td>
                      <td>{loan.loan_amount}</td>
                      <td>₹{loan.emi_amount || "-"}</td>
                      <td>{formatDate(loan.disbursed_date) || "-"}</td>
                      <td>{formatDate(loan.create_date)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="8" className="text-center">No loans found</td></tr>
              )}
            </tbody>
          </Table>
          )}
          </TabPane>

          {/* ---- INSTALLMENT & COLLECTION (merged) ---- collection summary + full installment section */}
          <TabPane tabId="installment">
            {/* Collection breakdown (payment table + amort adjustment) with FIP / PIF scheme filter */}
            <Card className="mb-3" style={{ borderRadius: "0.5rem", background: "#f6f9fc" }}>
              <CardBody className="py-3">
                <div className="d-flex justify-content-end mb-2">
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${collectionScheme === "pif" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setCollectionScheme("pif")}
                    >
                      PIF
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${collectionScheme === "fip" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setCollectionScheme("fip")}
                    >
                      FIP
                    </button>
                  </div>
                </div>
                <Row>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Total Collection</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#2e7d32" }}>
                      {/* Total = Principal + Interest + Penal so it always reconciles with the split. */}
                      ₹{money(
                        collectionTotal
                          ? num(collectionTotal.principalCollection) +
                              num(collectionTotal.interestCollection) +
                              num(collectionTotal.feeCollection)
                          : totalCollection
                      )}
                    </div>
                  </Col>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Principal Received</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#1e4fbf" }}>
                      ₹{money(collectionTotal ? collectionTotal.principalCollection : 0)}
                    </div>
                  </Col>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Interest Received</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#11998e" }}>
                      ₹{money(collectionTotal ? collectionTotal.interestCollection : 0)}
                    </div>
                  </Col>
                  <Col xs="6" md="3" className="mb-2 mb-md-0">
                    <div className="text-uppercase text-muted small font-weight-bold">Penal Charge Settled</div>
                    <div className="h3 mb-0 font-weight-bold" style={{ color: "#d9534f" }}>
                      {/* Penal charge = type_id 3 from the amort adjustment table. */}
                      ₹{money(collectionTotal ? collectionTotal.feeCollection : 0)}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            {/* Full installment section: Payment History, FIP, PIF, Add Payment, Enach, Statement */}
            <Installment />
          </TabPane>
        </TabContent>
      </CardBody>
    </Card>
  );
};
export default Profile;

